import { useCallback, useMemo, useState } from 'react'
import { callAllHandlers, isFunction } from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey, noop } from '@mote/helpers/constants'
import { compareItems, rankItem, rankings } from '@tanstack/match-sorter-utils'
import {
  createTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  sortingFns,
  useTableInstance
} from '@tanstack/react-table'
import { Checkbox } from '../checkbox'

const table = createTable().setOptions({
  filterFns: {
    fuzzy: (row, columnId, value, addMeta) => {
      const itemRank = rankItem(row.getValue(columnId), value, {
        threshold: rankings.MATCHES
      })

      addMeta(itemRank)

      return itemRank.passed
    }
  },
  sortingFns: {
    fuzzy: (rowA, rowB, columnId) => {
      let dir = 0

      if (rowA.columnFiltersMeta[columnId]) {
        dir = compareItems(
          rowA.columnFiltersMeta[columnId],
          rowB.columnFiltersMeta[columnId]
        )
      }

      return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir
    }
  }
})

const JUSTIFY_MAP = {
  left: 'start',
  center: 'center',
  right: 'end',
  justify: 'stretch',
  initial: 'initial',
  inherit: 'inherit'
}

function getSortCellJustification(value) {
  const justify = JUSTIFY_MAP[value] ?? JUSTIFY_MAP.initial
  const layout =
    justify === JUSTIFY_MAP.right ? 'horizontal-reverse' : 'horizontal'

  return { justify, layout }
}

export const useDataTableFunctions = ({
  data = { rows: [], pageIndex: 0, pageSize: 10, pages: [], pageCount: 0 },
  columns: _columns = [],
  isSearchable = false,
  isPaginatable = false,
  isManualPagination = false,
  isSortable = false,
  isRowSelectable = false,
  searchBarProps = {},
  headerCellProps = {},
  rowProps = {},
  cellProps = {},
  onPageChange = noop,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState(searchBarProps.value || '')
  const [rowSelection, setRowSelection] = useState({})
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: data.pageIndex,
    pageSize: data.pageSize
  })

  const columns = useMemo(() => {
    const columns = _columns.map(column => {
      let { cell, header } = column

      if (!isFunction(cell)) cell = info => column.cell ?? info.getValue()
      if (!isFunction(header)) header = () => column.header

      switch (column.type) {
        case 'data':
          return table.createDataColumn(column.id, {
            header,
            cell,
            enableSorting: isSortable && column.enableSorting,
            sortingFn: 'fuzzy'
          })
        case 'display': {
          const options = {
            id: column.id,
            header
          }

          if (column.cell) {
            options.cell = column.cell
          }

          return table.createDisplayColumn(options)
        }
      }
    })

    if (isRowSelectable)
      columns.unshift(
        table.createDisplayColumn({
          id: 'select',
          header: ({ instance }) => (
            <Checkbox
              size='md'
              {...{
                checked: instance.getIsAllRowsSelected(),
                indeterminate: instance.getIsSomeRowsSelected(),
                onChange: instance.getToggleAllRowsSelectedHandler()
              }}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              size='md'
              {...{
                checked: row.getIsSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          )
        })
      )

    return columns
  }, [_columns, isRowSelectable, isSortable])

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize
    }),
    [pageIndex, pageSize]
  )

  const options = useMemo(() => {
    const options = {
      data: data.rows,
      columns,
      state: {},
      getCoreRowModel: getCoreRowModel()
    }

    if (isSortable) {
      options.getSortedRowModel = getSortedRowModel()
    }

    if (isPaginatable) {
      options.getPaginationRowModel = getPaginationRowModel()

      if (isManualPagination) {
        options.onPaginationChange = setPagination
        options.state.pagination = pagination
        options.manualPagination = true
        options.pageCount = data.pageCount ?? -1
      }
    }

    if (isRowSelectable) {
      options.state.rowSelection = rowSelection
      options.onRowSelectionChange = setRowSelection
    }

    if (isSearchable) {
      options.getFilteredRowModel = getFilteredRowModel()
      options.onGlobalFilterChange = setSearchTerm
      options.state.globalFilter = searchTerm
      options.globalFilterFn = 'fuzzy'
    }

    return options
  }, [
    data.rows,
    data.pageCount,
    columns,
    isSortable,
    isPaginatable,
    isRowSelectable,
    isSearchable,
    pagination,
    isManualPagination,
    rowSelection,
    searchTerm
  ])

  const instance = useTableInstance(table, options)

  const getTableProps = useCallback(() => {
    return {
      borderless: true,
      ...props
    }
  }, [props])

  const getHeaderCellProps = useCallback(
    header => {
      const columnId = header.column.id
      const item = header.isPlaceholder ? null : header.renderHeader()
      const canSort = isSortable && header.column.getCanSort()
      const {
        isIndividualProps = false,
        commonProps = {},
        ...props
      } = headerCellProps
      const cellProps = isIndividualProps
        ? columnId in props
          ? { ...commonProps, ...props[columnId] }
          : commonProps
        : props
      let sortProps = {}

      if (canSort) {
        const onClick = event => {
          preventBubblingEvent(event)
          return header.column.getToggleSortingHandler()(event)
        }
        const justificationProps = getSortCellJustification(cellProps.align)
        const sortDirection = header.column.getIsSorted() || null

        sortProps = {
          ...justificationProps,
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: event => {
            switch (event.keyCode) {
              case KeyboardKey.Enter:
              case KeyboardKey.Space:
                onClick(event)
            }
          },
          'data-sort': sortDirection,
          sortDirection
        }
      }

      return {
        ...cellProps,
        sortProps,
        item,
        isSortable: canSort,
        colSpan: header.colSpan
      }
    },
    [isSortable, headerCellProps]
  )

  const getRowProps = useCallback(
    row => {
      const isSelected = row.getIsSelected()
      const onClick = isRowSelectable ? row.getToggleSelectedHandler() : null
      const selectableProps = isRowSelectable
        ? { cursor: 'pointer', userSelect: 'none' }
        : {}

      return {
        ...rowProps,
        isSelected,
        onClick,
        ...selectableProps,
        isHoverable: isRowSelectable
      }
    },
    [isRowSelectable, rowProps]
  )

  const getCellProps = useCallback(
    cell => {
      const {
        isIndividualProps = false,
        commonProps = {},
        ...props
      } = cellProps
      const columnId = cell.column.id

      if (isIndividualProps) {
        return columnId in props
          ? { ...commonProps, ...props[columnId] }
          : commonProps
      }

      return props
    },
    [cellProps]
  )

  const getSearchBarProps = useCallback(() => {
    return {
      ...searchBarProps,
      value: searchTerm ?? '',
      onChange: callAllHandlers(searchBarProps.onChange, event =>
        setSearchTerm(String(event.target.value))
      )
    }
  }, [searchTerm, searchBarProps])

  return {
    getTableProps,
    getHeaderCellProps,
    getCellProps,
    getRowProps,
    getSearchBarProps,
    onPageChange,
    isSearchable,
    isRowSelectable,
    isSortable,
    isPaginatable,
    isManualPagination,
    table: instance,
    data,
    columns
  }
}
